import { useEffect, useMemo } from 'react';

import type { MaintenanceWorkOrderListQuery } from '@gb-ticket/shared-types';

export type MaintenanceWorkOrderSortBy = MaintenanceWorkOrderListQuery['sortBy'];

export type MaintenanceWorkOrderListSortScope = 'home' | 'planning' | 'archive' | 'trash';

export type MaintenanceWorkOrderListSort = {
  sortBy: MaintenanceWorkOrderSortBy;
  sortOrder: 'asc' | 'desc';
};

const SORT_STORAGE_PREFIX = 'gb-ticket.maintenance.work-orders.sort.v2.';

export const MAINTENANCE_HOME_SORT_FIELDS: readonly MaintenanceWorkOrderSortBy[] = [
  'orderNumber',
  'customerName',
  'installationName',
  'scheduledFor',
  'dueAt',
  'status',
];

export const MAINTENANCE_PLANNING_SORT_FIELDS: readonly MaintenanceWorkOrderSortBy[] = [
  'orderNumber',
  'customerName',
  'installationName',
  'assignedToName',
  'scheduledFor',
  'dueAt',
  'status',
];

export const MAINTENANCE_ARCHIVE_SORT_FIELDS: readonly MaintenanceWorkOrderSortBy[] = [
  'orderNumber',
  'installationName',
  'assignedToName',
  'scheduledFor',
  'completedAt',
  'status',
];

export function isMaintenanceWorkOrderSortBy(
  value: string,
  allowed: readonly MaintenanceWorkOrderSortBy[],
): value is MaintenanceWorkOrderSortBy {
  return (allowed as readonly string[]).includes(value);
}

function sortStorageKey(scope: MaintenanceWorkOrderListSortScope): string {
  return `${SORT_STORAGE_PREFIX}${scope}`;
}

export function readMaintenanceWorkOrderListSort(
  scope: MaintenanceWorkOrderListSortScope,
  allowed: readonly MaintenanceWorkOrderSortBy[],
): MaintenanceWorkOrderListSort | null {
  if (typeof localStorage === 'undefined') {
    return null;
  }
  try {
    const raw = localStorage.getItem(sortStorageKey(scope));
    if (!raw) {
      return null;
    }
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') {
      return null;
    }
    const sortBy = 'sortBy' in parsed ? parsed.sortBy : undefined;
    const sortOrder = 'sortOrder' in parsed ? parsed.sortOrder : undefined;
    if (typeof sortBy !== 'string' || !isMaintenanceWorkOrderSortBy(sortBy, allowed)) {
      return null;
    }
    if (sortOrder !== 'asc' && sortOrder !== 'desc') {
      return null;
    }
    return { sortBy, sortOrder };
  } catch {
    return null;
  }
}

export function writeMaintenanceWorkOrderListSort(
  scope: MaintenanceWorkOrderListSortScope,
  sort: MaintenanceWorkOrderListSort,
): void {
  if (typeof localStorage === 'undefined') {
    return;
  }
  localStorage.setItem(sortStorageKey(scope), JSON.stringify(sort));
}

function searchHasSort(search: string): boolean {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  return params.has('sortBy') || params.has('sortOrder');
}

/** URL sort wins. Without it, the last column sort for this list is restored. */
export function withRememberedWorkOrderSort<T extends MaintenanceWorkOrderListQuery>(
  search: string,
  scope: MaintenanceWorkOrderListSortScope,
  allowed: readonly MaintenanceWorkOrderSortBy[],
  query: T,
): T {
  if (searchHasSort(search)) {
    return query;
  }
  const stored = readMaintenanceWorkOrderListSort(scope, allowed);
  if (!stored) {
    return query;
  }
  return { ...query, sortBy: stored.sortBy, sortOrder: stored.sortOrder };
}

export function useRememberedWorkOrderSort<T extends MaintenanceWorkOrderListQuery>(
  search: string,
  scope: MaintenanceWorkOrderListSortScope,
  allowed: readonly MaintenanceWorkOrderSortBy[],
  query: T,
): T {
  const resolved = useMemo(
    () => withRememberedWorkOrderSort(search, scope, allowed, query),
    [search, scope, allowed, query],
  );

  useEffect(() => {
    if (!searchHasSort(search)) {
      return;
    }
    if (!isMaintenanceWorkOrderSortBy(resolved.sortBy, allowed)) {
      return;
    }
    writeMaintenanceWorkOrderListSort(scope, {
      sortBy: resolved.sortBy,
      sortOrder: resolved.sortOrder,
    });
  }, [allowed, resolved.sortBy, resolved.sortOrder, scope, search]);

  return resolved;
}
